import React, { useState, useEffect } from 'react';
import { SettingsIcon, CloseIcon, TrashIcon, AgentIcon, SwarmIcon, GraphIcon, FolderIcon, ToolsIcon, ChatIcon } from './Icons';
import CustomDropdown from './CustomDropdown';
import MarkdownEditorModal from './MarkdownEditorModal';

function PropertyPanel({ 
  node, 
  onUpdate, 
  onDelete, 
  onSetEntryPoint, 
  providers, 
  workflowType,
  workflowMetadata,
  swarmConfig,
  onWorkflowUpdate,
  onClose,
  builtinTools = []
}) {
  const [config, setConfig] = useState(node?.data?.config || {});
  const [label, setLabel] = useState(node?.data?.label || '');
  const [description, setDescription] = useState(node?.data?.description || '');
  const [color, setColor] = useState(node?.data?.color || '#6b7280');

  const isEntryPoint = node?.data?.isEntryPoint || false;
  const isGroup = node?.type === 'group';

  useEffect(() => {
    if (node) {
      setConfig(node.data.config || {});
      setLabel(node.data.label || '');
      setDescription(node.data.description || '');
      setColor(node.data.color || '#6b7280');
    }
  }, [node]);

  const updateConfig = (path, value) => {
    const baseConfig = node?.data?.config || {};
    const newConfig = JSON.parse(JSON.stringify(baseConfig));
    const keys = path.split('.');
    let current = newConfig;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
    onUpdate({ config: newConfig });
  };

  const getValue = (path) => {
    const keys = path.split('.');
    let value = config;
    for (let key of keys) {
      value = value?.[key];
    }
    return value;
  };

  // When no node is selected, show workflow settings
  if (!node) {
    return (
      <div className="flex flex-col" style={{ height: '100%' }}>
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">
              <SettingsIcon size={14} color="#60D2CC" />
            </div>
            <span>Workflow Settings</span>
          </div>
          <button className="panel-toggle" onClick={onClose} title="Close panel">
            <CloseIcon size={12} />
          </button>
        </div>
        <div className="panel-content" style={{ padding: 'var(--spacing-md)', paddingLeft: 0, overflowY: 'auto' }}>
          <WorkflowSettings
            workflowType={workflowType}
            workflowMetadata={workflowMetadata}
            swarmConfig={swarmConfig}
            onUpdate={onWorkflowUpdate}
          />
        </div>
      </div>
    );
  }

  // Only show for agent or group nodes
  if (node.type !== 'agent' && node.type !== 'group') {
    return (
      <div className="flex flex-col" style={{ height: '100%' }}>
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">
              <SettingsIcon size={14} color="#60D2CC" />
            </div>
            <span>Properties</span>
          </div>
          <button className="panel-toggle" onClick={onClose} title="Close panel">
            <CloseIcon size={12} />
          </button>
        </div>
        <div className="panel-content" style={{ padding: 'var(--spacing-xl)', paddingLeft: 0 }}>
          <div className="empty-state">
            <div className="empty-state-text">
              This node type cannot be configured
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: '100%' }}>
      {/* Header */}
      <div className="panel-header">
        <div className="panel-title">
          <div className="panel-title-icon" style={{
            background: isGroup
              ? node.data.subtype === 'swarm'
                ? 'rgba(245, 158, 11, 0.2)'
                : node.data.subtype === 'graph'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : 'var(--bg-hover)'
              : 'rgba(96, 210, 204, 0.2)',
            border: isGroup
              ? node.data.subtype === 'swarm'
                ? '1px solid rgba(245, 158, 11, 0.4)'
                : node.data.subtype === 'graph'
                  ? '1px solid rgba(59, 130, 246, 0.4)'
                  : '1px solid var(--border-primary)'
              : '1px solid rgba(96, 210, 204, 0.4)'
          }}>
            {isGroup
              ? (node.data.subtype === 'swarm'
                ? <SwarmIcon size={14} color="#F59E0B" />
                : node.data.subtype === 'graph'
                  ? <GraphIcon size={14} color="#3B82F6" />
                  : <FolderIcon size={14} color="#999" />)
              : <AgentIcon size={14} color="#60D2CC" />
            }
          </div>
          <span>{isGroup ? `${node.data.subtype} Group` : 'Agent'}</span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
          <button
            className="btn btn-sm btn-danger"
            onClick={onDelete}
            title="Delete node"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <TrashIcon size={14} />
          </button>
          <button className="panel-toggle" onClick={onClose} title="Close panel">
            <CloseIcon size={12} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="panel-content" style={{ paddingLeft: 0 }}>
        {/* Group-specific properties */}
        {isGroup && (
          <>
            <div className="section">
              <div className="section-title">
                <SettingsIcon size={14} color="#60D2CC" />
                <span>Group Settings</span>
              </div>

              <div className="input-group">
                <label className="input-label">Name</label>
                <input
                  type="text"
                  className="input"
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);
                    onUpdate({ label: e.target.value });
                  }}
                  placeholder="Enter group name"
                />
              </div>

              <div className="input-group">
                <label className="input-label">Description</label>
                <textarea
                  className="input"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    onUpdate({ description: e.target.value });
                  }}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Color</label>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value);
                      onUpdate({ color: e.target.value });
                    }}
                    style={{
                      width: '48px',
                      height: '36px',
                      cursor: 'pointer',
                      background: 'transparent',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-sm)'
                    }}
                  />
                  <input
                    type="text"
                    className="input"
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value);
                      onUpdate({ color: e.target.value });
                    }}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Contains</label>
                <div className="badge" style={{ fontSize: '12px' }}>
                  {node.data.childNodes?.length || 0} nodes
                </div>
              </div>
            </div>

            {node.data.subtype === 'swarm' && (
              <div className="section">
                <div className="section-title">
                  <SwarmIcon size={14} color="#F59E0B" />
                  <span>Swarm Config</span>
                </div>

                <div className="input-group">
                  <label className="input-label">Max Handoffs</label>
                  <input
                    type="number"
                    className="input"
                    value={config.max_handoffs || 20}
                    onChange={(e) => updateConfig('max_handoffs', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                  <div className="input-hint">Maximum agent-to-agent handoffs</div>
                </div>

                <div className="input-group">
                  <label className="input-label">Execution Timeout (sec)</label>
                  <input
                    type="number"
                    className="input"
                    value={config.execution_timeout || 900}
                    onChange={(e) => updateConfig('execution_timeout', parseInt(e.target.value))}
                    min="60"
                    max="7200"
                  />
                  <div className="input-hint">Total workflow timeout (default: 15 min)</div>
                </div>

                <div className="input-group">
                  <label className="input-label">Node Timeout (sec)</label>
                  <input
                    type="number"
                    className="input"
                    value={config.node_timeout || 900}
                    onChange={(e) => updateConfig('node_timeout', parseInt(e.target.value))}
                    min="60"
                    max="3600"
                  />
                  <div className="input-hint">Per-agent timeout (default: 15 min)</div>
                </div>
              </div>
            )}

            {node.data.subtype === 'graph' && (
              <div className="section">
                <div className="section-title">
                  <GraphIcon size={14} color="#3B82F6" />
                  <span>Graph Config</span>
                </div>

                <div className="alert alert-info" style={{ marginBottom: 'var(--spacing-md)' }}>
                  Connect agents inside this group to define the workflow structure. Click edges to add conditions.
                </div>

                <div className="input-group">
                  <label className="input-label">Max Node Executions</label>
                  <input
                    type="number"
                    className="input"
                    value={config.max_node_executions || 50}
                    onChange={(e) => updateConfig('max_node_executions', parseInt(e.target.value))}
                    min="1"
                    max="1000"
                  />
                  <div className="input-hint">Prevents infinite loops in cyclic graphs</div>
                </div>

                <div className="input-group">
                  <label className="input-label">Execution Timeout (sec)</label>
                  <input
                    type="number"
                    className="input"
                    value={config.execution_timeout || 900}
                    onChange={(e) => updateConfig('execution_timeout', parseInt(e.target.value))}
                    min="1"
                    max="3600"
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Node Timeout (sec)</label>
                  <input
                    type="number"
                    className="input"
                    value={config.node_timeout || ''}
                    onChange={(e) => updateConfig('node_timeout', e.target.value ? parseInt(e.target.value) : null)}
                    min="1"
                    placeholder="No limit"
                  />
                </div>

                <div className="input-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.reset_on_revisit || false}
                      onChange={(e) => updateConfig('reset_on_revisit', e.target.checked)}
                    />
                    Reset on Revisit
                  </label>
                  <div className="input-hint">Reset node state when revisited in cycles</div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Agent-specific properties */}
        {!isGroup && (
          <AgentProperties
            config={config}
            updateConfig={updateConfig}
            getValue={getValue}
            providers={providers}
            workflowType={workflowType}
            isEntryPoint={isEntryPoint}
            onSetEntryPoint={onSetEntryPoint}
            color={color}
            setColor={setColor}
            onUpdate={onUpdate}
            builtinTools={builtinTools}
          />
        )}
      </div>
    </div>
  );
}

function AgentProperties({ config, updateConfig, getValue, providers, workflowType, isEntryPoint, onSetEntryPoint, color, setColor, onUpdate, builtinTools = [] }) {
  const tools = getValue('tools') || [];
  const [showSystemPromptModal, setShowSystemPromptModal] = useState(false);
  
  // Extract full KB configs for assigned KB tools (tools that start with 'kb_')
  // Include ALL Pinecone config so backend can generate complete tool spec with namespaces/filters
  const assignedKbConfigs = tools
    .filter(toolId => toolId.startsWith('kb_'))
    .map(toolId => {
      // Find the full KB tool config in builtinTools
      const kbTool = builtinTools.find(t => t.id === toolId && t.isCustomKb);
      if (kbTool) {
        return {
          tool_name: kbTool.name,
          tool_description: kbTool.description,
          id: kbTool.kbId,
          name: kbTool.name,
          // Full Pinecone config for backend
          pinecone_index_name: kbTool.pinecone_index_name,
          pinecone_host: kbTool.pinecone_host,
          pinecone_namespaces: kbTool.pinecone_namespaces,
          embedding_model: kbTool.embedding_model,
          metadata_schema: kbTool.metadata_schema,
        };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <>
      {/* Entry Point Control */}
      {workflowType !== 'single' && (
        <div className="section">
          <div
            className={`alert ${isEntryPoint ? 'alert-success' : 'alert-warning'}`}
            style={{ cursor: 'pointer' }}
            onClick={onSetEntryPoint}
          >
            <label className="checkbox-label" style={{
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              <input
                type="checkbox"
                checked={isEntryPoint}
                onChange={() => { }}
                style={{ width: '18px', height: '18px' }}
              />
              {isEntryPoint ? '▶ Entry Point Agent' : 'Set as Entry Point'}
            </label>
            <div style={{
              fontSize: '11px',
              marginTop: '4px',
              marginLeft: '26px',
              opacity: 0.8
            }}>
              {isEntryPoint
                ? 'This agent will be executed first'
                : 'Click to make this the starting agent'}
            </div>
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div className="section">
        <div className="section-title">
          <SettingsIcon size={14} color="#60D2CC" />
          <span>Basic Info</span>
        </div>

        <div className="input-group">
          <label className="input-label">Agent Name</label>
          <input
            type="text"
            className="input font-mono"
            value={getValue('name') || ''}
            onChange={(e) => updateConfig('name', e.target.value)}
            placeholder="my_agent"
          />
          <div className="input-hint">Unique identifier for this agent</div>
        </div>

        <div className="input-group">
          <label className="input-label">Description</label>
          <textarea
            className="input"
            value={getValue('description') || ''}
            onChange={(e) => updateConfig('description', e.target.value)}
            placeholder="What does this agent do?"
            rows={2}
          />
          <div className="input-hint">Helps other agents understand this agent's capabilities</div>
        </div>

        <div className="input-group">
          <label className="input-label">Color</label>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
            <input
              type="color"
              value={color || '#60D2CC'}
              onChange={(e) => {
                setColor(e.target.value);
                onUpdate({ color: e.target.value });
              }}
              style={{
                width: '48px',
                height: '36px',
                cursor: 'pointer',
                background: 'transparent',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-sm)'
              }}
            />
            <input
              type="text"
              className="input"
              value={color || '#60D2CC'}
              onChange={(e) => {
                setColor(e.target.value);
                onUpdate({ color: e.target.value });
              }}
              style={{ flex: 1 }}
            />
          </div>
          <div className="input-hint">Customize the agent's border, glow, and icon colors</div>
        </div>

        <div className="input-group">
          <label className="input-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>System Prompt</span>
            <button 
              className="expand-btn" 
              onClick={() => setShowSystemPromptModal(true)}
              title="Open in full editor"
            >
              ⛶
            </button>
          </label>
          <textarea
            className="input input-mono"
            value={getValue('system_prompt') || ''}
            onChange={(e) => updateConfig('system_prompt', e.target.value)}
            placeholder="You are a helpful AI assistant..."
            rows={4}
          />
          <div className="input-hint">Instructions that guide the agent's behavior (click ⛶ for full editor)</div>
        </div>
        
        {/* Markdown Editor Modal */}
        <MarkdownEditorModal
          isOpen={showSystemPromptModal}
          onClose={() => setShowSystemPromptModal(false)}
          value={getValue('system_prompt') || ''}
          onChange={(newValue) => updateConfig('system_prompt', newValue)}
          title={`Edit System Prompt - ${getValue('name') || 'Agent'}`}
          tools={tools}
          agentName={getValue('name') || 'Agent'}
          workflowType={workflowType || 'agent'}
          knowledgeBases={assignedKbConfigs}
        />
      </div>

      {/* Model Configuration */}
      <div className="section">
        <div className="section-title">
          <AgentIcon size={14} color="#60D2CC" />
          <span>Model</span>
        </div>

        <div className="input-group">
          <label className="input-label">Provider</label>
          <CustomDropdown
            value={getValue('model.provider') || 'bedrock'}
            options={providers.map(p => ({ value: p.id, label: p.name }))}
            onChange={(value) => updateConfig('model.provider', value)}
            placeholder="Select provider"
          />
        </div>

        <div className="input-group">
          <label className="input-label">Model ID</label>
          <CustomDropdown
            value={getValue('model.model_id') || ''}
            options={(providers.find(p => p.id === (getValue('model.provider') || 'bedrock'))?.models || []).map(m => ({ value: m, label: m }))}
            onChange={(value) => updateConfig('model.model_id', value)}
            placeholder="Select model"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
          <div className="input-group">
            <label className="input-label">Temperature</label>
            <input
              type="number"
              className="input"
              step="0.1"
              min="0"
              max="2"
              value={getValue('model.temperature') || 0.7}
              onChange={(e) => updateConfig('model.temperature', parseFloat(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label className="input-label">Max Tokens</label>
            <input
              type="number"
              className="input"
              step="100"
              min="100"
              max="200000"
              value={getValue('model.max_tokens') || 4096}
              onChange={(e) => updateConfig('model.max_tokens', parseInt(e.target.value))}
            />
          </div>
        </div>

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={getValue('model.streaming') !== false}
              onChange={(e) => updateConfig('model.streaming', e.target.checked)}
            />
            Enable streaming responses
          </label>
        </div>

        <div className="input-group">
          <label className="input-label">Agent Timeout (sec)</label>
          <input
            type="number"
            className="input"
            value={getValue('timeout') || ''}
            onChange={(e) => updateConfig('timeout', e.target.value ? parseInt(e.target.value) : null)}
            min="60"
            max="3600"
            placeholder="Use swarm default (900)"
          />
          <div className="input-hint">Override per-agent timeout (blank = use swarm default)</div>
        </div>
      </div>

      {/* Conversation Manager */}
      <div className="section">
        <div className="section-title">
          <ChatIcon size={14} color="#60D2CC" />
          <span>Memory & Context</span>
        </div>

        <div className="input-group">
          <label className="input-label">Manager Type</label>
          <CustomDropdown
            value={getValue('conversation_manager.type') || 'sliding_window'}
            options={[
              { value: 'sliding_window', label: 'Sliding Window', hint: 'Basic - keeps last N messages' },
              { value: 'smart', label: 'Smart (Recommended)', hint: 'Preserves important info, smart summarization' },
              { value: 'swarm', label: 'Swarm-Aware', hint: 'For multi-agent handoffs' },
              { value: 'summarizing', label: 'Summarizing', hint: 'Compresses old messages with LLM' },
              { value: 'none', label: 'None', hint: 'No context management' }
            ]}
            onChange={(value) => updateConfig('conversation_manager.type', value)}
            placeholder="Select manager type"
          />
          <div className="input-hint">
            {getValue('conversation_manager.type') === 'smart' && 'Detects & preserves decisions, facts, and important info'}
            {getValue('conversation_manager.type') === 'swarm' && 'Optimized for multi-agent context sharing'}
            {getValue('conversation_manager.type') === 'sliding_window' && 'Simple window - drops old messages'}
            {getValue('conversation_manager.type') === 'summarizing' && 'Summarizes old context when threshold exceeded'}
          </div>
        </div>

        {/* Settings for sliding_window */}
        {getValue('conversation_manager.type') === 'sliding_window' && (
          <>
          <div className="input-group">
            <label className="input-label">Window Size</label>
            <input
              type="number"
              className="input"
              min="1"
                max="100"
                value={getValue('conversation_manager.window_size') || getValue('conversation_manager.max_messages') || 40}
                onChange={(e) => updateConfig('conversation_manager.window_size', parseInt(e.target.value))}
            />
              <div className="input-hint">Messages to keep in history (default: 40)</div>
          </div>
            <div className="input-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={getValue('conversation_manager.should_truncate_results') !== false}
                  onChange={(e) => updateConfig('conversation_manager.should_truncate_results', e.target.checked)}
                />
                Truncate Large Tool Results
              </label>
              <div className="input-hint">Shorten tool outputs to save context space</div>
            </div>
          </>
        )}

        {/* Settings for smart and swarm */}
        {(getValue('conversation_manager.type') === 'smart' || getValue('conversation_manager.type') === 'swarm') && (
          <>
            <div className="input-group">
              <label className="input-label">Window Size</label>
              <input
                type="number"
                className="input"
                min="5"
                max="50"
                value={getValue('conversation_manager.window_size') || 20}
                onChange={(e) => updateConfig('conversation_manager.window_size', parseInt(e.target.value))}
              />
              <div className="input-hint">Recent messages to keep in full detail (default: 20)</div>
            </div>
            <div className="input-group">
              <label className="input-label">Summary Threshold</label>
              <input
                type="number"
                className="input"
                min="10"
                max="100"
                value={getValue('conversation_manager.summary_threshold') || 35}
                onChange={(e) => updateConfig('conversation_manager.summary_threshold', parseInt(e.target.value))}
              />
              <div className="input-hint">Trigger summarization when messages exceed this (default: 35)</div>
            </div>
            <div className="input-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={getValue('conversation_manager.preserve_important') !== false}
                  onChange={(e) => updateConfig('conversation_manager.preserve_important', e.target.checked)}
                />
                Preserve Important Info
              </label>
              <div className="input-hint">Extract & keep decisions, constraints, and key facts</div>
            </div>
          </>
        )}

        {/* Settings for summarizing */}
        {getValue('conversation_manager.type') === 'summarizing' && (
          <>
            <div className="input-group">
              <label className="input-label">Summary Ratio</label>
              <input
                type="number"
                className="input"
                min="0.1"
                max="0.8"
                step="0.1"
                value={getValue('conversation_manager.summary_ratio') || 0.3}
                onChange={(e) => updateConfig('conversation_manager.summary_ratio', parseFloat(e.target.value))}
              />
              <div className="input-hint">Fraction of messages to summarize (0.1-0.8)</div>
            </div>
            <div className="input-group">
              <label className="input-label">Preserve Recent</label>
              <input
                type="number"
                className="input"
                min="2"
                max="20"
                value={getValue('conversation_manager.preserve_recent_messages') || 6}
                onChange={(e) => updateConfig('conversation_manager.preserve_recent_messages', parseInt(e.target.value))}
              />
              <div className="input-hint">Minimum recent messages to keep intact</div>
            </div>
          </>
        )}
      </div>

      {/* Assigned Tools */}
      <div className="section">
        <div className="section-title">
          <ToolsIcon size={14} color="#60D2CC" />
          <span>Tools</span>
          {tools.length > 0 && (
            <span className="badge badge-success">{tools.length}</span>
          )}
        </div>

        {tools.length === 0 ? (
          <div className="alert alert-warning">
            No tools assigned. Use the Tool Library panel to add tools.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)' }}>
            {tools.map((toolId, idx) => (
              <div key={idx} className="tool-tag">
                <ToolsIcon size={12} color="#60D2CC" />
                <span>{toolId}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Advanced: Structured Output */}
      <div className="section">
        <div className="section-title">
          <span>📝</span>
          <span>Structured Output</span>
        </div>

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={getValue('structured_output.enabled') || false}
              onChange={(e) => updateConfig('structured_output.enabled', e.target.checked)}
            />
            Force structured JSON responses
          </label>
        </div>

        {getValue('structured_output.enabled') && (
          <div className="input-group">
            <label className="input-label">Output Schema (JSON Schema)</label>
            <textarea
              className="input input-mono"
              value={getValue('structured_output.schema') || ''}
              onChange={(e) => updateConfig('structured_output.schema', e.target.value)}
              rows={6}
              placeholder={`{
  "type": "object",
  "properties": {
    "name": {"type": "string"}
  }
}`}
            />
          </div>
        )}
      </div>

      {/* Advanced: Hooks */}
      <div className="section">
        <div className="section-title">
          <span>🪝</span>
          <span>Hooks</span>
        </div>

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={getValue('hooks.logging') || false}
              onChange={(e) => updateConfig('hooks.logging', e.target.checked)}
            />
            Enable logging hook
          </label>
          <div className="input-hint">Log all agent events</div>
        </div>

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={getValue('hooks.metrics') || false}
              onChange={(e) => updateConfig('hooks.metrics', e.target.checked)}
            />
            Enable metrics hook
          </label>
          <div className="input-hint">Track performance metrics</div>
        </div>
      </div>
    </>
  );
}

/**
 * WorkflowSettings - Global workflow configuration
 * Shown when no agent is selected
 */
function WorkflowSettings({ workflowType, workflowMetadata, swarmConfig, onUpdate }) {
  const metadata = workflowMetadata || {};
  const config = swarmConfig || {};

  const updateMetadata = (key, value) => {
    if (onUpdate) {
      onUpdate({
        metadata: { ...metadata, [key]: value }
      });
    }
  };

  const updateSwarmConfig = (key, value) => {
    if (onUpdate) {
      onUpdate({
        swarm_config: { ...config, [key]: value }
      });
    }
  };

  return (
    <>
      {/* Workflow Info */}
      <div className="section">
        <div className="section-title">
          <SettingsIcon size={14} color="#60D2CC" />
          <span>Workflow Info</span>
        </div>

        <div className="input-group">
          <label className="input-label">Name</label>
          <input
            type="text"
            className="input"
            value={metadata.name || ''}
            onChange={(e) => updateMetadata('name', e.target.value)}
            placeholder="My Workflow"
          />
        </div>

        <div className="input-group">
          <label className="input-label">Description</label>
          <textarea
            className="input"
            value={metadata.description || ''}
            onChange={(e) => updateMetadata('description', e.target.value)}
            placeholder="What does this workflow do?"
            rows={2}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Workflow Type</label>
          <div className="badge badge-info" style={{ display: 'inline-block' }}>
            {workflowType === 'swarm' ? '🔀 Swarm (Handoffs)' :
             workflowType === 'graph' ? '📊 Graph (Edges)' :
             '🤖 Single Agent'}
          </div>
          <div className="input-hint">Change by adding/removing agents and edges</div>
        </div>
      </div>

      {/* Swarm/Graph Configuration */}
      {(workflowType === 'swarm' || workflowType === 'graph') && (
        <div className="section">
          <div className="section-title">
            <SwarmIcon size={14} color="#60D2CC" />
            <span>Multi-Agent Limits</span>
          </div>

          <div className="input-group">
            <label className="input-label">Max Handoffs</label>
            <input
              type="number"
              className="input"
              min="1"
              max="50"
              value={config.max_handoffs || 15}
              onChange={(e) => updateSwarmConfig('max_handoffs', parseInt(e.target.value))}
            />
            <div className="input-hint">Maximum agent-to-agent handoffs allowed (default: 15)</div>
          </div>

          <div className="input-group">
            <label className="input-label">Max Iterations</label>
            <input
              type="number"
              className="input"
              min="1"
              max="50"
              value={config.max_iterations || 20}
              onChange={(e) => updateSwarmConfig('max_iterations', parseInt(e.target.value))}
            />
            <div className="input-hint">Maximum loop iterations before stopping (default: 20)</div>
          </div>

          <div className="input-group">
            <label className="input-label">Execution Timeout (sec)</label>
            <input
              type="number"
              className="input"
              min="60"
              max="3600"
              step="60"
              value={config.execution_timeout || 900}
              onChange={(e) => updateSwarmConfig('execution_timeout', parseFloat(e.target.value))}
            />
            <div className="input-hint">Total workflow timeout in seconds (default: 900 = 15 min)</div>
          </div>

          <div className="input-group">
            <label className="input-label">Node Timeout (sec)</label>
            <input
              type="number"
              className="input"
              min="30"
              max="600"
              step="30"
              value={config.node_timeout || 240}
              onChange={(e) => updateSwarmConfig('node_timeout', parseFloat(e.target.value))}
            />
            <div className="input-hint">Per-agent timeout in seconds (default: 240 = 4 min)</div>
          </div>
        </div>
      )}

      {/* Memory & Context Info */}
      <div className="section">
        <div className="section-title">
          <ChatIcon size={14} color="#60D2CC" />
          <span>Memory Info</span>
        </div>

        <div className="alert alert-info">
          <strong>🧠 Project Memory</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
            Each project has a <code>.threedium</code> folder that stores:
          </p>
          <ul style={{ margin: '4px 0 0 16px', fontSize: '12px', opacity: 0.9, paddingLeft: '0' }}>
            <li>• Decisions made across sessions</li>
            <li>• Key facts about the project</li>
            <li>• User preferences</li>
            <li>• Session summaries</li>
          </ul>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
            Agents can use <code>load_project_context</code> and <code>record_decision</code> tools to access this memory.
          </p>
        </div>
      </div>

      {/* Tips */}
      <div className="section">
        <div className="section-title">
          <span>💡</span>
          <span>Tips</span>
        </div>

        <div className="empty-state-text" style={{ fontSize: '12px', lineHeight: '1.6' }}>
          <p><strong>Click an agent</strong> to edit its properties, model, system prompt, and memory settings.</p>
          <p style={{ marginTop: '8px' }}><strong>For best context retention:</strong></p>
          <ul style={{ marginLeft: '16px', marginTop: '4px' }}>
            <li>Use "Smart" conversation manager for entry agents</li>
            <li>Add memory tools: <code>load_project_context</code>, <code>record_decision</code></li>
            <li>Instruct agents to check context at session start</li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default PropertyPanel;
